import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { setToken, getToken, setUnauthorizedHandler } from "../api/client";

export type AuthUser = {
  id: string;
  email: string;
};

type AuthContextType = {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setTokenState(null);
      setUser(null);
    });
  }, []);

  useEffect(() => {
    const saved = getToken();
    if (!saved) {
      setLoading(false);
      return;
    }
    fetch("/api/auth/me", { headers: { Authorization: `Bearer ${saved}` } })
      .then(async (res) => {
        if (res.ok) {
          const u = await res.json();
          setTokenState(saved);
          setUser({ id: String(u.id), email: String(u.email) });
        } else {
          setToken(null);
        }
      })
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  function login(newToken: string, newUser: AuthUser): void {
    setToken(newToken);
    setTokenState(newToken);
    setUser(newUser);
  }

  function logout(): void {
    setToken(null);
    setTokenState(null);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
